import { DateTimeISO, Money, PaginatedResponse, User } from './common';
import {
  TRANSACTION_DELIVERY,
  TRANSACTION_REQUEST_STATUS,
} from './transactionRequests';
import {
  AccountOrigins,
  OwnershipType,
  TRANSACTION_TYPE,
} from './transactions';

export type TransactionReviewStatus =
  | 'under review'
  | 'canceled'
  | 'approved'
  | 'declined';

export type TransactionReviewReasons =
  | 'exceeds transaction limits'
  | 'exceeds request threshold'
  | 'no prior wire transactions';

export type TransactionReviewPreview = {
  uuid: string;
  requestedAt: DateTimeISO;
  reviewReasons: TransactionReviewReasons[];
  amount: Money;
  description: string;
  displayAmount: string;
  deliverySpeed: TRANSACTION_DELIVERY;
  owner: User;
  bankAccountOwners: User[];
};

export type TransactionReviewListResponse =
  PaginatedResponse<TransactionReviewPreview>;

type TransactionReviewReviewSummaryBase = {
  reviewReasons: TransactionReviewReasons[];
  requestedBy: User;
  lastModifiedAt: DateTimeISO | null;
  lastModifiedBy: User | null;
  dueDiligenceNotes: string | null;
};
type TransactionReviewReviewSummaryApproved =
  TransactionReviewReviewSummaryBase & {
    reviewStatus: 'approved';
    approvedBy: User; //populates when approved by a STAFF user
    canceledBy: null;
    declinedBy: null;
  };
type TransactionReviewReviewSummaryCanceled =
  TransactionReviewReviewSummaryBase & {
    reviewStatus: 'canceled';
    approvedBy: null;
    canceledBy: User;
    declinedBy: null;
  };
type TransactionReviewReviewSummaryDeclined =
  TransactionReviewReviewSummaryBase & {
    reviewStatus: 'declined';
    approvedBy: null;
    canceledBy: null;
    declinedBy: User;
  };
type TransactionReviewReviewSummaryUnderReview =
  TransactionReviewReviewSummaryBase & {
    reviewStatus: 'under review';
    approvedBy: null;
    canceledBy: null;
    declinedBy: null;
  };

export type TransactionReviewReviewSummary =
  | TransactionReviewReviewSummaryApproved
  | TransactionReviewReviewSummaryCanceled
  | TransactionReviewReviewSummaryDeclined
  | TransactionReviewReviewSummaryUnderReview;

export type AccountDetails = {
  bankAccountOrigin: AccountOrigins;
  ownershipType: OwnershipType | null;
  bankLogoUrl: string | null;
};

export type TransactionReviewTransactionDetails = {
  status: TRANSACTION_REQUEST_STATUS;
  transactionType: TRANSACTION_TYPE;
  requestedAt: DateTimeISO;
  requestedBy: User;
  expirationAt: string;
  approvedAt: DateTimeISO | null;
  approvedBy: User | null;
  declinedAt: DateTimeISO | null;
  declinedBy: User | null;
  canceledAt: DateTimeISO | null;
  canceledBy: User | null;
  owner: User;
  amount: Money;
  displayAmount: string;
  fromAccount: string;
  fromAccountDetails: AccountDetails;
  toAccount: string;
  toAccountDetails: AccountDetails;
  deliverySpeed: TRANSACTION_DELIVERY;
  description: string;
  note: string | null;
  originationMethod: string | null;
  initiatedByAuthorizedTeammate: boolean;
  skipCustomerReview: boolean;
};

// if approved by a STAFF user, ApprovedBy info appears in the `reviewSummary`
// if approved by an ADVISOR, ApprovedBy info appears in the `transactionDetails`
export type TransactionReviewDetails = {
  reviewSummary: TransactionReviewReviewSummary;
  transactionDetails: TransactionReviewTransactionDetails;
};

export type ApproveDeclineTransactionReviewRequestBody = {
  id: string;
  dueDiligenceNotes: string;
};

export type TransactionReviewListQueryParams = {
  page: number;
  status?: TransactionReviewStatus;
  records_per_page?: number;
};

export const queryKeys = {
  list: ['transactionReviewList'] as const,
  details: ['transcationReviewDetails'] as const,
};
